import { TableColumnProp } from '../types/table-column.type';
import { getterForProp } from './column-prop-getters';

export type OptionalValueGetter = (row: unknown) => unknown | undefined;
export function optionalGetterForProp(prop: TableColumnProp): OptionalValueGetter {
  return prop && ((row: unknown): unknown => getterForProp(prop)(row, prop));
}

/**
 * This functions rearrange items by their parents
 * Also sets the level value to each of the items
 *
 * Note: Expecting each item has a property called parentId
 * Note: This algorithm will fail if a list has two or more items with same ID
 * NOTE: This algorithm will fail if there is a deadlock of relationship
 *
 * For example,
 *
 * Input
 *
 * id -> parent
 * 1  -> 0
 * 2  -> 0
 * 3  -> 1
 * 4  -> 1
 * 5  -> 2
 * 7  -> 8
 * 6  -> 3
 *
 *
 * Output
 * id -> level
 * 1      -> 0
 * --3    -> 1
 * ----6  -> 2
 * --4    -> 1
 * 2      -> 0
 * --5    -> 1
 * 7     -> 8
 *
 *
 * @param rows
 *
 */
export function groupRowsByParents(rows: Array<unknown>, from?: OptionalValueGetter, to?: OptionalValueGetter): Array<unknown> {
  if (from && to) {
    const nodeById: Record<string | number, TreeNode> = {};
    const l = rows.length;

    nodeById[0] = new TreeNode(); // that's the root node

    const uniqIDs = rows.reduce((arr: Array<unknown>, item: unknown) => {
      const toValue = to(item);
      if (arr.indexOf(toValue) === -1) {
        arr.push(toValue);
      }
      return arr;
    }, []);

    for (let i = 0; i < l; i++) {
      // make TreeNode objects for each item
      nodeById[to(rows[i]) as string | number] = new TreeNode(rows[i]);
    }

    for (let i = 0; i < l; i++) {
      // link all TreeNode objects
      const node = nodeById[to(rows[i]) as string | number];
      let parent = 0;
      const fromValue = from(node.row);
      if (!!fromValue && uniqIDs.indexOf(fromValue) > -1) {
        parent = fromValue as number;
      }
      node.parent = nodeById[parent];
      (node.row as Record<string, unknown>)['level'] = (node.parent.row as Record<string, unknown>)['level'] as number + 1;
      node.parent.children.push(node);
    }

    let resolvedRows: Array<unknown> = [];
    nodeById[0].flatten(function(this: TreeNode) {
      resolvedRows = [...resolvedRows, this.row];
    }, true);

    return resolvedRows;
  } else {
    return rows;
  }
}

class TreeNode {
  public row: unknown;
  public parent: TreeNode;
  public children: Array<TreeNode>;

  constructor(row: unknown | null = null) {
    if (!row) {
      row = {
        level: -1,
        treeStatus: 'expanded'
      };
    }
    this.row = row;
    this.parent = null;
    this.children = [];
  }

  flatten(f: (this: TreeNode) => void, recursive: boolean): void {
    if ((this.row as Record<string, unknown>)['treeStatus'] === 'expanded') {
      for (let i = 0, l = this.children.length; i < l; i++) {
        const child = this.children[i];
        f.call(child);
        if (recursive) { child.flatten(f, recursive); }
      }
    }
  }
}
